import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { of, Subject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, filter, map, switchMap, takeUntil } from 'rxjs/operators';
import { emtrConstant } from '../../constants/emtrConstants';
import { EmtrService } from '../../emtr.service';
import { LinkDelinkService, SharedConstant, SharedService } from '@ems/shared';
import { RuleService } from '../../services/rule-service/rule.service';
import { RestrictionSimulatorService } from '../../services/restriction-simulator-service/restriction-simulator.service';
import { searchSecuritiesTableHeads } from '../../models/search-securities-table-header.model';

interface RestrictionSimulatorSecurityRow {
  itemIssueId: number | null;
  action: string;
  tickerSymbol: string;
  name: string;
  securityType: string;
  putCall: string;
  cusip: string;
  sedol: string;
  isin: string;
}

interface RestrictionSimulatorSearchRecord {
  id?: string;
  tickerSymbol?: string;
  symbol?: string;
  name?: string;
  description?: string;
  securityType?: string;
  type?: string;
  putCall?: string;
  cusip?: string;
  sedol?: string;
  isin?: string;
  ssid?: number | string;
  SSID?: number | string;
  securityRow?: RestrictionSimulatorSearchRecord;
}

interface RestrictionSimulatorTradeValidationPayload {
  brokerageAcctId: number;
  masterAccounts: number[];
  orders: Array<{
    seqNum: number;
    orderLegs: Array<{
      itemIssueId: number;
      buyOrSell: string;
      securityType: string;
      openClosPostnCd?: string;
      putCall?: string;
    }>;
  }>;
}

type RestrictionSimulatorPayloadMode = 'thematic' | 'standardNonOptions' | 'standardOptions';
type RestrictionSimulatorSummaryStatus = 'ALLOWED' | 'RESTRICTED' | '';

interface RestrictionSimulatorDetailRow {
  itemIssueId: number | null;
  tickerSymbol: string;
  action: string;
  securityTypeLabel: string;
  status: 'ALLOWED' | 'RESTRICTED';
  message: string;
}

interface RestrictionSimulatorApplicableRule {
  ssid?: number | string;
  rules?: Array<{
    ruleId?: number | string;
    ruleName?: string | null;
  }>;
}

interface RestrictionSimulatorApplicableRuleEvaluation {
  hasRuleEntries: boolean;
  hasOverrideFound: boolean;
  hasOverrideFoundInOtherOrg: boolean;
  restrictionRuleNames: string[];
}

@Component({
  selector: 'tr-restriction-simulator',
  templateUrl: './restriction-simulator.component.html',
  styleUrls: ['./restriction-simulator.component.scss']
})
export class RestrictionSimulatorComponent implements OnInit, OnDestroy {
  emtrConstant = emtrConstant;
  sharedConstant = SharedConstant;
  private readonly sharedSecurityTypeList = this.emtrConstant.instrumentGroups.instrumentGrpStep3.securityTypeList;
  private readonly accountLookupErrorKey = 'invalidAccountNumberLookup';
  private readonly restrictionSimulatorDomain = this.emtrConstant.restrictionSimulator.domain;
  private readonly securityTypeCode = {
    eq: this.sharedSecurityTypeList.find(({ value }) => value === 'EQ')?.value || 'EQ',
    etf: this.sharedSecurityTypeList.find(({ value }) => value === 'ETF')?.value || 'ETF',
    mf: this.sharedSecurityTypeList.find(({ value }) => value === 'MF')?.value || 'MF',
    opt: this.sharedSecurityTypeList.find(({ value }) => value === 'OPT')?.value || 'OPT',
    fi: this.emtrConstant.restrictionSimulator.fixedIncomeSecurityTypeLabel || 'FI'
  } as const;
  private readonly securityTypeLabelToCode = new Map(
    this.sharedSecurityTypeList.map(({ label, value }) => [((label || '') as string).toUpperCase(), value])
  );
  private readonly securityTypeValueToCode = new Map(
    this.sharedSecurityTypeList.map(({ value }) => [((value || '') as string).toUpperCase(), value])
  );
  private readonly securityTypeAliasToCode: Record<string, string> = {
    [this.restrictionSimulatorDomain.securitySearchType.stock]: this.securityTypeCode.eq,
    [this.restrictionSimulatorDomain.securitySearchType.option]: this.securityTypeCode.opt,
    BOND: this.securityTypeCode.fi,
    'MUTUAL FUND': this.securityTypeCode.mf,
    'MUTUAL FUNDS': this.securityTypeCode.mf,
    EQUITY: this.securityTypeCode.eq,
    OPTION: this.securityTypeCode.opt
  };
  hasRestrictionSimulatorActionAccess = false;
  restrictionSimulatorForm: UntypedFormGroup;
  orderTypeOptions = [
    { label: this.restrictionSimulatorDomain.orderType.standard, value: this.restrictionSimulatorDomain.orderType.standard },
    { label: this.restrictionSimulatorDomain.orderType.thematic, value: this.restrictionSimulatorDomain.orderType.thematic }
  ];
  securityTypeOptions = [
    { label: this.restrictionSimulatorDomain.securityType.nonOptions, value: this.restrictionSimulatorDomain.securityType.nonOptions },
    {
      label: this.sharedSecurityTypeList.find(({ value }) => value === this.securityTypeCode.opt)?.label || 'Options',
      value: this.restrictionSimulatorDomain.securityType.options
    }
  ];
  nonOptionsTradeActionOptions = [
    { label: this.restrictionSimulatorDomain.action.buy, value: this.restrictionSimulatorDomain.action.buy },
    { label: this.restrictionSimulatorDomain.action.sell, value: this.restrictionSimulatorDomain.action.sell },
    { label: this.restrictionSimulatorDomain.action.shortSell, value: this.restrictionSimulatorDomain.action.shortSell }
  ];
  optionsTradeActionOptions = [
    { label: this.restrictionSimulatorDomain.action.buyToOpen, value: this.restrictionSimulatorDomain.action.buyToOpen },
    { label: this.restrictionSimulatorDomain.action.buyToClose, value: this.restrictionSimulatorDomain.action.buyToClose },
    { label: this.restrictionSimulatorDomain.action.sellToOpen, value: this.restrictionSimulatorDomain.action.sellToOpen },
    { label: this.restrictionSimulatorDomain.action.sellToClose, value: this.restrictionSimulatorDomain.action.sellToClose }
  ];
  nonOptionsNoShortSellActions = [
    { label: this.restrictionSimulatorDomain.action.buy, value: this.restrictionSimulatorDomain.action.buy },
    { label: this.restrictionSimulatorDomain.action.sell, value: this.restrictionSimulatorDomain.action.sell }
  ];
  selectedSecuritiesTableColumns = [
    { field: 'action', header: searchSecuritiesTableHeads.find((column) => column.field === 'actions')?.header || 'Action' },
    ...searchSecuritiesTableHeads
      .filter((column) => column.field !== 'actions')
      .map((column) => ({ field: column.field || '', header: column.header || '' })),
    { field: 'remove', header: '' }
  ];
  securitySearchText = '';
  securitySearchResults: RestrictionSimulatorSearchRecord[] = [];
  showSecuritySearchNoRecords = false;
  selectedSecurities: RestrictionSimulatorSecurityRow[] = [];
  masterAccountOptions: Array<{ label: string; value: string; accountType: string; accountSubType: string }> = [];
  isMasterAccountDisabled = false;
  isSubmittingRestrictionSimulator = false;
  validationSummaryStatus: RestrictionSimulatorSummaryStatus = '';
  private validationSummaryDefaultStatus: RestrictionSimulatorSummaryStatus = '';
  private validationSummaryRuleName = '';
  private readonly pcisSuperRestrictionRuleName = 'PCIS Super Restriction Rule found';
  viewDetailsExpanded = false;
  validationDetailRows: RestrictionSimulatorDetailRow[] = [];
  private readonly nonOptionsMaxSecurities = 1;
  private readonly optionsMaxSecurities = 4;
  private readonly thematicMaxSecurities = 30;
  private readonly destroy$ = new Subject<void>();
  private readonly securitySearchInput$ = new Subject<string>();
  private readonly accountLookupDebounceMs = 300;
  private readonly securitySearchDebounceMs = 2000;
  private selectedClientInfo: any;
  private lastValidatedAccountNumber = '';
  private lastConfirmedAccountNumber = '';

  constructor(public readonly emtrService: EmtrService,
              private readonly sharedService: SharedService,
              private readonly linkDelinkService: LinkDelinkService,
              private readonly ruleService: RuleService,
              private readonly restrictionSimulatorService: RestrictionSimulatorService,
              private readonly formBuilder: UntypedFormBuilder,
              @Inject('entitlementConst') public entitlementConst) {}

  ngOnInit() {
    this.initializeRestrictionSimulatorForm();
    this.subscribeToFormChanges();
    this.subscribeToAccountNumberChanges();
    this.subscribeToSecurityTypeChanges();
    this.subscribeToOrderTypeChanges();
    this.subscribeToSecuritySearch();
    this.emtrService.clientInformation
      .pipe(takeUntil(this.destroy$))
      .subscribe((clientInfo) => {
        this.selectedClientInfo = clientInfo;
        this.masterAccountOptions = [];
        this.sharedService.clearErrorMessage();
        this.clearRestrictionSimulatorForm();
        this.setRestrictionSimulatorActionAccess();
        this.loadMasterAccounts(clientInfo);
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  validateAccountNumber(markAsTouched = true) {
    const accountNumberControl = this.restrictionSimulatorForm.get('accountNumber');
    const normalizedAccountNumber = this.getNormalizedAccountNumber(accountNumberControl.value);

    if (markAsTouched) {
      accountNumberControl.markAsTouched();
    }

    if (normalizedAccountNumber && normalizedAccountNumber === this.lastValidatedAccountNumber) {
      return;
    }

    accountNumberControl.updateValueAndValidity({ emitEvent: false });

    this.lastValidatedAccountNumber = normalizedAccountNumber;
    this.clearAccountLookupError(accountNumberControl);
    this.clearConfirmedAccountNumberIfStale(normalizedAccountNumber);

    if (!normalizedAccountNumber || !this.selectedClientInfo?.orgId) {
      return;
    }

    if (!this.isEligibleForAccountLookup(normalizedAccountNumber)) {
      return;
    }

    const payload = {
      active: true,
      accountNum: normalizedAccountNumber
    };

    this.sharedService.getEmployeeList({ pageNo: 0, pageSize: 1 }, payload, null, null, this.selectedClientInfo.orgId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          if (this.getNormalizedAccountNumber(accountNumberControl.value) !== normalizedAccountNumber) {
            return;
          }

          const accounts = Array.isArray(response?.data?.content) ? response.data.content : [];
          if (!accounts.length) {
            this.lastConfirmedAccountNumber = '';
            this.setAccountLookupError(accountNumberControl);
            return;
          }

          this.lastConfirmedAccountNumber = normalizedAccountNumber;
          this.clearAccountLookupError(accountNumberControl);
        },
        error: () => {
          if (this.getNormalizedAccountNumber(accountNumberControl.value) !== normalizedAccountNumber) {
            return;
          }
          this.lastConfirmedAccountNumber = '';
          this.setAccountLookupError(accountNumberControl);
        }
      });
  }

  get isAccountNumberInvalid(): boolean {
    const accountNumberControl = this.restrictionSimulatorForm?.get('accountNumber');
    return !!accountNumberControl && accountNumberControl.hasError(this.accountLookupErrorKey);
  }

  get isAccountNumberLengthInvalid(): boolean {
    const normalizedAccountNumber = this.getNormalizedAccountNumber(this.restrictionSimulatorForm?.get('accountNumber')?.value);
    return !!normalizedAccountNumber && !this.isEligibleForAccountLookup(normalizedAccountNumber);
  }

  get accountNumberErrorMessage(): string {
    if (this.isAccountNumberLengthInvalid) {
      return this.sharedConstant.textFieldErrorMsg.accountNumberShouldContain8Digits;
    }

    return this.emtrConstant.restrictionSimulator.accountNumberNotFoundMessage;
  }

  get accountNumberFieldLabel(): string {
    const businessUnit = this.selectedClientInfo?.businessUnit || this.sharedConstant.rbsBUText;
    return this.sharedConstant[businessUnit]?.empOrParticipantsAccount || this.sharedConstant[this.sharedConstant.rbsBUText].empOrParticipantsAccount;
  }

  clearRestrictionSimulatorForm() {
    this.sharedService.clearErrorMessage();
    this.ruleService.toastMsg.emit('');
    this.ruleService.toastType.emit('');

    const accountNumberControl = this.restrictionSimulatorForm.get('accountNumber');
    accountNumberControl.setValue('');
    accountNumberControl.markAsPristine();
    accountNumberControl.markAsUntouched();
    this.lastValidatedAccountNumber = '';
    this.lastConfirmedAccountNumber = '';
    this.clearAccountLookupError(accountNumberControl);
    this.restrictionSimulatorForm.get('orderType').setValue(this.orderTypeOptions[0].value);
    this.restrictionSimulatorForm.get('securityType').setValue(this.securityTypeOptions[0].value);
    this.restrictionSimulatorForm.get('securityType').enable({ emitEvent: false });
    this.restrictionSimulatorForm.get('securitySearch').setValue('');
    this.securitySearchText = '';
    this.securitySearchResults = [];
    this.showSecuritySearchNoRecords = false;
    this.selectedSecurities = [];
    this.clearValidationMessages();
    this.applyDefaultMasterAccountSelection();
  }

  autocompleteSecuritySearch(event: { query: string }) {
    if (this.selectedSecurities.length >= this.maxSecuritiesAllowed) {
      return;
    }

    let query = event?.query || '';
    if (!query.trim()) {
      this.securitySearchResults = [];
      this.showSecuritySearchNoRecords = false;
      return;
    }

    query = `${query}`;
    this.securitySearchInput$.next(query);
  }

  private subscribeToSecuritySearch() {
    this.securitySearchInput$
      .pipe(
        debounceTime(this.securitySearchDebounceMs),
        switchMap((query) => this.ruleService.getSearchSecurities({
          securitySearch: query,
          securityType: this.getSecuritySearchTypeFilters(),
          page: 1,
          size: 50
        })
          .pipe(
            map((securities) => ({
              query,
              records: this.toAutocompleteSuggestions(this.extractSecurityRecords(securities))
            })),
            catchError(() => of({ query, records: [] }))
          )),
        takeUntil(this.destroy$)
      )
      .subscribe(({ query, records }) => {
        this.securitySearchResults = records;
        this.showSecuritySearchNoRecords = !!query?.trim() && records.length === 0;
      });
  }

  private getSecuritySearchTypeFilters(): string[] {
    if (this.isThematicOrder) {
      return [this.restrictionSimulatorDomain.securitySearchType.stock];
    }

    const selectedSecurityType = this.restrictionSimulatorForm.get('securityType').value;
    if (selectedSecurityType === this.restrictionSimulatorDomain.securityType.options) {
      return [this.restrictionSimulatorDomain.securitySearchType.option];
    }

    return this.restrictionSimulatorDomain.restrictionSimulatorSearchSecurityType.nonOptions
      .split(',')
      .map((value) => value.trim())
      .filter((value) => !!value);
  }

  selectedSecurityValue(event: { value: RestrictionSimulatorSearchRecord }) {
    this.clearValidationMessages();
    this.showSecuritySearchNoRecords = false;
    if (this.selectedSecurities.length >= this.maxSecuritiesAllowed) {
      return;
    }

    const selectedSecurity = event?.value?.securityRow || event?.value;
    if (!selectedSecurity) {
      return;
    }

    const mappedSecurity = this.mapSelectedSecurity(selectedSecurity);
    const mappedSecurityKey = this.getSecurityKey(mappedSecurity);
    const isDuplicate = this.selectedSecurities.some((security) => this.getSecurityKey(security) === mappedSecurityKey);

    if (isDuplicate) {
      this.securitySearchText = '';
      this.restrictionSimulatorForm.get('securitySearch').setValue('');
      return;
    }

    this.selectedSecurities = [...this.selectedSecurities, mappedSecurity];
    this.securitySearchText = '';
    this.restrictionSimulatorForm.get('securitySearch').setValue('');
  }

  removeSelectedSecurity(index: number) {
    this.clearValidationMessages();
    this.showSecuritySearchNoRecords = false;
    this.selectedSecurities.splice(index, 1);
    this.selectedSecurities = [...this.selectedSecurities];
    this.securitySearchText = '';
    this.securitySearchResults = [];
    this.restrictionSimulatorForm.get('securitySearch').setValue('');
  }

  get isSecurityLimitReached(): boolean {
    return this.selectedSecurities.length >= this.maxSecuritiesAllowed;
  }

  get securitySearchPlaceholder(): string {
    if (this.isSecurityLimitReached) {
      const securityLabel = this.maxSecuritiesAllowed === 1 ? 'security' : 'securities';
      return `Maximum of ${this.maxSecuritiesAllowed} ${securityLabel} reached.`;
    }

    return this.emtrConstant.restrictionSimulator.securitySearchPlaceholder;
  }

  get hasSelectedSecurity(): boolean {
    return this.selectedSecurities.length > 0;
  }

  get validationSummaryLabel(): string {
    if (this.validationSummaryStatus === 'ALLOWED') {
      return 'Allowed';
    }

    if (this.validationSummaryStatus === 'RESTRICTED') {
      return 'Restricted';
    }

    return '';
  }

  get validationSummaryDescription(): string {
    if (this.validationSummaryStatus === 'RESTRICTED') {
      if (this.isStandardNonOptionsOrder) {
        if (this.validationSummaryRuleName) {
          if (this.validationSummaryRuleName
            .toLowerCase()
            .includes(this.pcisSuperRestrictionRuleName.toLowerCase())) {
            return this.pcisSuperRestrictionRuleName;
          }
          return `Trade order cannot be executed due to active restriction rule: ${this.validationSummaryRuleName}.`;
        }
        return 'Trade order cannot be executed due to restrictions on another organization\'s Master account linked to this account.';
      }
      return 'One or more trade orders cannot be executed due to active restrictions.';
    }

    return '';
  }

  get validationAllowedSummaryDescription(): string {
    if (this.validationSummaryStatus !== 'ALLOWED') {
      return '';
    }

    const hasOtherOrgOverride = this.validationDetailRows.some(
      (row) => row.status === 'ALLOWED'
        && row.message === 'Trade order is allowed due to an override on another organization\'s Master account linked to this account.'
    );

    if (hasOtherOrgOverride) {
      if (!this.isStandardNonOptionsOrder && this.validationSummaryDefaultStatus === 'ALLOWED') {
        return 'Trade order has no active restrictions.';
      }
      return 'Trade order is allowed due to an override on another organization\'s Master account linked to this account.';
    }

    return 'Trade order has no active restrictions.';
  }

  get showValidationSummaryDescription(): boolean {
    return this.validationSummaryStatus === 'RESTRICTED' && !!this.validationSummaryDescription;
  }

  get showValidationDetailsToggle(): boolean {
    return (this.isThematicOrder || this.isStandardOptionsOrder)
      && this.validationSummaryStatus === 'RESTRICTED'
      && this.validationDetailRows.length > 0;
  }

  get isValidationSummaryExpanded(): boolean {
    const summaryMessage = this.validationSummaryStatus === 'ALLOWED'
      ? this.validationAllowedSummaryDescription
      : this.validationSummaryDescription;

    return this.shouldExpandValidationMessage(summaryMessage);
  }

  private get isStandardNonOptionsOrder(): boolean {
    return !this.isThematicOrder
      && this.restrictionSimulatorForm.get('securityType').value === this.restrictionSimulatorDomain.securityType.nonOptions;
  }

  private get isStandardOptionsOrder(): boolean {
    return !this.isThematicOrder
      && this.restrictionSimulatorForm.get('securityType').value === this.restrictionSimulatorDomain.securityType.options;
  }

  toggleValidationDetails() {
    this.viewDetailsExpanded = !this.viewDetailsExpanded;
  }

  isValidationDetailExpanded(message: string): boolean {
    return this.shouldExpandValidationMessage(message);
  }

  get canSubmitRestrictionSimulator(): boolean {
    return this.restrictionSimulatorForm.valid && this.hasSelectedSecurity && this.hasConfirmedAccountNumber;
  }

  onSubmitRestrictionSimulator() {
    this.sharedService.clearErrorMessage();
    if (this.hasMissingItemIssueId()) {
      this.ruleService.toastType.emit('warn');
      this.ruleService.toastMsg.emit('Unable to validate trade. Missing itemIssueId for one or more selected securities.');
      return;
    }

    const payload = this.prepareTradeValidationPayload();
    if (!payload) {
      this.ruleService.toastType.emit('warn');
      this.ruleService.toastMsg.emit(this.emtrConstant.restrictionSimulator.submitEligibilityWarningMessage);
      return;
    }
    this.clearValidationMessages();
    this.isSubmittingRestrictionSimulator = true;
    this.restrictionSimulatorService.submitTradeValidation(payload, {}, this.selectedClientInfo?.orgId, this.selectedClientInfo?.businessUnit)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          const isTimeoutResponse = this.processTradeValidationResponse(response);
          if (isTimeoutResponse) {
            this.ruleService.toastType.emit('error');
            this.ruleService.toastMsg.emit(this.emtrConstant.restrictionSimulator.requestTimeoutMessage);
          }
          this.isSubmittingRestrictionSimulator = false;
        },
        error: (error) => {
          this.clearValidationResultState();
          const backendMessage = error?.error?.errorResponse?.errorMessage
            || error?.error?.message
            || error?.message
            || '';
          const message = backendMessage || this.emtrConstant.restrictionSimulator.requestFailedDefaultMessage;
          this.ruleService.toastType.emit('error');
          this.ruleService.toastMsg.emit(message);
          this.isSubmittingRestrictionSimulator = false;
        }
      });
  }

  private shouldExpandValidationMessage(message: string): boolean {
    return (message || '').trim().length > 120;
  }

  get isThematicOrder(): boolean {
    return this.restrictionSimulatorForm.get('orderType').value === this.restrictionSimulatorDomain.orderType.thematic;
  }

  private get maxSecuritiesAllowed(): number {
    if (this.isThematicOrder) {
      return this.thematicMaxSecurities;
    }

    return this.restrictionSimulatorForm.get('securityType').value === this.restrictionSimulatorDomain.securityType.options
      ? this.optionsMaxSecurities
      : this.nonOptionsMaxSecurities;
  }

  private initializeRestrictionSimulatorForm() {
    this.restrictionSimulatorForm = this.formBuilder.group({
      accountNumber: ['', Validators.required],
      masterAccounts: [[], Validators.required],
      orderType: [this.orderTypeOptions[0].value, Validators.required],
      securityType: [this.securityTypeOptions[0].value, Validators.required],
      securitySearch: ['']
    });
  }

  private subscribeToFormChanges() {
    this.restrictionSimulatorForm.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.clearValidationMessages();
        this.clearStaleAccountLookupError();
      });
  }

  private subscribeToAccountNumberChanges() {
    this.restrictionSimulatorForm.get('accountNumber').valueChanges
      .pipe(
        debounceTime(this.accountLookupDebounceMs),
        map((accountNumber: string) => this.getNormalizedAccountNumber(accountNumber)),
        distinctUntilChanged(),
        filter((normalizedAccountNumber: string) => this.isEligibleForAccountLookup(normalizedAccountNumber)),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.validateAccountNumber(false);
      });
  }

  private getNormalizedAccountNumber(accountNumber: string): string {
    return ((accountNumber || '') as string).replace(/-/g, '');
  }

  private isEligibleForAccountLookup(accountNumber: string): boolean {
    return accountNumber.length === 8 || accountNumber.length === 9;
  }

  private get hasConfirmedAccountNumber(): boolean {
    const normalizedAccountNumber = this.getNormalizedAccountNumber(this.restrictionSimulatorForm?.get('accountNumber')?.value);
    return this.isEligibleForAccountLookup(normalizedAccountNumber) && normalizedAccountNumber === this.lastConfirmedAccountNumber;
  }

  private clearStaleAccountLookupError() {
    const accountNumberControl = this.restrictionSimulatorForm?.get('accountNumber');
    const normalizedAccountNumber = this.getNormalizedAccountNumber(accountNumberControl?.value);
    this.clearConfirmedAccountNumberIfStale(normalizedAccountNumber);

    if (accountNumberControl?.hasError(this.accountLookupErrorKey) && normalizedAccountNumber !== this.lastValidatedAccountNumber) {
      this.clearAccountLookupError(accountNumberControl);
    }
  }

  private clearConfirmedAccountNumberIfStale(normalizedAccountNumber: string) {
    if (normalizedAccountNumber !== this.lastConfirmedAccountNumber) {
      this.lastConfirmedAccountNumber = '';
    }
  }

  private clearAccountLookupError(control: AbstractControl) {
    const currentErrors = control.errors;
    if (!currentErrors?.[this.accountLookupErrorKey]) {
      return;
    }

    const { [this.accountLookupErrorKey]: removedError, ...remainingErrors } = currentErrors;
    void removedError;
    control.setErrors(Object.keys(remainingErrors).length ? remainingErrors : null);
  }

  private setAccountLookupError(control: AbstractControl) {
    const currentErrors = control.errors || {};
    control.setErrors({
      ...currentErrors,
      [this.accountLookupErrorKey]: true
    });
  }

  private subscribeToSecurityTypeChanges() {
    this.restrictionSimulatorForm.get('securityType').valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.clearValidationMessages();
        this.showSecuritySearchNoRecords = false;
        this.securitySearchResults = [];
        this.securitySearchText = '';
        this.selectedSecurities = [];
        this.restrictionSimulatorForm.get('securitySearch').setValue('');
      });
  }

  private subscribeToOrderTypeChanges() {
    this.restrictionSimulatorForm.get('orderType').valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((orderType: string) => {
        this.clearValidationMessages();
        this.showSecuritySearchNoRecords = false;
        this.securitySearchResults = [];
        this.securitySearchText = '';
        this.selectedSecurities = [];
        this.restrictionSimulatorForm.get('securitySearch').setValue('');

        if (orderType === this.restrictionSimulatorDomain.orderType.thematic) {
          this.restrictionSimulatorForm.get('securityType').disable({ emitEvent: false });
        } else {
          this.restrictionSimulatorForm.get('securityType').enable({ emitEvent: false });
          if (!this.restrictionSimulatorForm.get('securityType').value) {
            this.restrictionSimulatorForm.get('securityType').setValue(this.securityTypeOptions[0].value);
          }
        }
      });
  }

  private loadMasterAccounts(clientInfo: any) {
    if (!clientInfo?.orgId || !clientInfo?.clientId || !this.hasRestrictionSimulatorActionAccess) {
      this.resetMasterAccounts();
      return;
    }

    const targetAccountType = clientInfo.businessUnit === this.sharedConstant.rbsBUText
      ? this.emtrConstant.ruleSteps.step3.k4Text
      : this.sharedConstant.slAccountTypeText;

    this.linkDelinkService.getLinkMasterAccounts(clientInfo.orgId, clientInfo.clientId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          const masterAccounts = Array.isArray(response?.data) ? response.data : [];
          const filteredMasterAccounts = masterAccounts
            .filter((masterAccount) => masterAccount.accountType === targetAccountType)
            .map((masterAccount) => ({
              label: masterAccount.clientLabel + ' - ' + masterAccount.masterAccountNbr,
              value: masterAccount.masterAccountNbr,
              accountType: masterAccount.accountType,
              accountSubType: masterAccount.accountSubType
            }));

          this.masterAccountOptions = filteredMasterAccounts;
          this.applyDefaultMasterAccountSelection();
        },
        error: () => {
          this.resetMasterAccounts();
        }
      });
  }

  private applyDefaultMasterAccountSelection() {
    const masterAccountsControl = this.restrictionSimulatorForm.get('masterAccounts');

    if (this.masterAccountOptions.length === 1) {
      this.isMasterAccountDisabled = true;
      masterAccountsControl.setValue([this.masterAccountOptions[0]]);
      masterAccountsControl.disable({ emitEvent: false });
    } else {
      this.isMasterAccountDisabled = false;
      masterAccountsControl.enable({ emitEvent: false });
      masterAccountsControl.setValue(this.masterAccountOptions.length > 1 ? [...this.masterAccountOptions] : []);
    }

    masterAccountsControl.markAsPristine();
    masterAccountsControl.markAsUntouched();
  }

  private resetMasterAccounts() {
    this.masterAccountOptions = [];
    this.isMasterAccountDisabled = false;
    const masterAccountsControl = this.restrictionSimulatorForm?.get('masterAccounts');
    if (masterAccountsControl) {
      masterAccountsControl.enable({ emitEvent: false });
      masterAccountsControl.setValue([]);
    }
  }

  private setRestrictionSimulatorActionAccess() {
    const navUuid = this.entitlementConst?.navUUID;
    const restrictionSimulatorEntitlementUuid = navUuid?.emtrAccess?.restrictionSimulatorSummary?.uuid;
    const hasRestrictionSimulatorEntitlement = this.sharedService.checkEntitlements(restrictionSimulatorEntitlementUuid, '', '');
    this.hasRestrictionSimulatorActionAccess = this.sharedService.isInternalApp() && hasRestrictionSimulatorEntitlement;
  }

  private extractSecurityRecords(response: { data?: RestrictionSimulatorSearchRecord[] } | null | undefined): RestrictionSimulatorSearchRecord[] {
    const pagedRecords = (response as any)?.data?.content;
    if (Array.isArray(pagedRecords)) {
      return pagedRecords;
    }

    if (Array.isArray(response?.data)) {
      return response.data;
    }

    return [];
  }

  private toAutocompleteSuggestions(records: RestrictionSimulatorSearchRecord[]): RestrictionSimulatorSearchRecord[] {
    return records.map((record) => ({
      ...record,
      id: record?.id || '',
      name: record?.name || ''
    }));
  }

  private getSecurityKey(record: RestrictionSimulatorSearchRecord | RestrictionSimulatorSecurityRow): string {
    const searchRecord = record as RestrictionSimulatorSearchRecord;
    const ticker = (record?.tickerSymbol || searchRecord?.id || searchRecord?.symbol || '').toString();
    const cusip = (record?.cusip || '').toString();
    const name = (record?.name || searchRecord?.description || '').toString();
    return `${ticker}|${cusip}|${name}`;
  }

  private getNormalizedSecurityType(securityType: string): string {
    const normalized = (securityType || '').toString().trim().toUpperCase();

    if (!normalized) {
      return this.securityTypeCode.eq;
    }

    const exactCodeMatch = this.securityTypeValueToCode.get(normalized);
    if (exactCodeMatch) {
      return exactCodeMatch;
    }

    const exactLabelMatch = this.securityTypeLabelToCode.get(normalized);
    if (exactLabelMatch) {
      return exactLabelMatch;
    }

    return this.securityTypeAliasToCode[normalized] || this.securityTypeCode.eq;
  }

  private mapSelectedSecurity(selectedSecurity: RestrictionSimulatorSearchRecord): RestrictionSimulatorSecurityRow {
    const source = selectedSecurity?.securityRow || selectedSecurity;
    const mappedSecurity = {
      itemIssueId: this.resolveItemIssueId(source),
      action: '',
      tickerSymbol: source?.tickerSymbol || source?.id || '',
      name: source?.name || '',
      securityType: source?.securityType || source?.type || '',
      putCall: this.resolvePutCall(source),
      cusip: source?.cusip || '',
      sedol: source?.sedol || '',
      isin: source?.isin || ''
    } as RestrictionSimulatorSecurityRow;
    const defaultAction = this.resolveTradeActionOptions(mappedSecurity)[0]?.value || this.restrictionSimulatorDomain.action.buy;
    return {
      ...mappedSecurity,
      action: defaultAction
    };
  }

  resolveTradeActionOptions(security?: RestrictionSimulatorSecurityRow): Array<{ label: string; value: string }> {
    if (this.isThematicOrder) {
      return [{ label: this.restrictionSimulatorDomain.action.buy, value: this.restrictionSimulatorDomain.action.buy }];
    }

    if (this.restrictionSimulatorForm.get('securityType').value === this.restrictionSimulatorDomain.securityType.options) {
      return this.optionsTradeActionOptions;
    }

    if (security) {
      const normalizedType = this.getNormalizedSecurityType(security.securityType);
      if (normalizedType === this.securityTypeCode.mf || normalizedType === this.securityTypeCode.fi) {
        return this.nonOptionsNoShortSellActions;
      }
    }

    return this.nonOptionsTradeActionOptions;
  }

  private prepareTradeValidationPayload(): RestrictionSimulatorTradeValidationPayload | null {
    const orderType = this.restrictionSimulatorForm.get('orderType').value;
    const securityType = this.restrictionSimulatorForm.get('securityType').value;

    if (orderType === this.restrictionSimulatorDomain.orderType.thematic) {
      return this.preparePayloadByMode('thematic');
    }

    if (orderType !== this.restrictionSimulatorDomain.orderType.standard) {
      return null;
    }

    if (securityType === this.restrictionSimulatorDomain.securityType.nonOptions) {
      return this.preparePayloadByMode('standardNonOptions');
    }

    if (securityType === this.restrictionSimulatorDomain.securityType.options) {
      return this.preparePayloadByMode('standardOptions');
    }

    return null;
  }

  private preparePayloadByMode(mode: RestrictionSimulatorPayloadMode): RestrictionSimulatorTradeValidationPayload {
    const payload = this.prepareBaseTradeValidationPayload();

    if (mode === 'thematic') {
      return {
        ...payload,
        orders: this.selectedSecurities.slice(0, this.thematicMaxSecurities).map((security, index) => ({
          seqNum: index + 1,
          orderLegs: [
            {
              itemIssueId: security.itemIssueId as number,
              buyOrSell: this.mapTradeActionToBuyOrSell(security.action),
              securityType: this.securityTypeCode.eq
            }
          ]
        }))
      };
    }

    if (mode === 'standardNonOptions') {
      return {
        ...payload,
        orders: [
          {
            seqNum: 1,
            orderLegs: this.selectedSecurities.map((security) => {
              const normalizedSecurityType = this.getNormalizedSecurityType(security.securityType);
              const securityTypeCode = normalizedSecurityType === this.securityTypeCode.etf
                || normalizedSecurityType === this.securityTypeCode.mf
                || normalizedSecurityType === this.securityTypeCode.fi
                ? normalizedSecurityType
                : this.securityTypeCode.eq;

              return {
                itemIssueId: security.itemIssueId as number,
                buyOrSell: this.mapTradeActionToBuyOrSell(security.action),
                securityType: securityTypeCode,
                openClosPostnCd: ' '
              };
            })
          }
        ]
      };
    }

    return {
      ...payload,
      orders: [
        {
          seqNum: 1,
          orderLegs: this.selectedSecurities.slice(0, this.optionsMaxSecurities).map((security) => ({
            securityType: this.securityTypeCode.opt,
            itemIssueId: security.itemIssueId as number,
            buyOrSell: this.mapTradeActionToBuyOrSell(security.action),
            putCall: security.putCall,
            openClosPostnCd: this.mapStandardOptionsOpenClose(security.action)
          }))
        }
      ]
    };
  }

  private prepareBaseTradeValidationPayload(): Omit<RestrictionSimulatorTradeValidationPayload, 'orders'> {
    const accountNumberRaw = ((this.restrictionSimulatorForm.get('accountNumber').value || '') as string).replace(/-/g, '');
    const brokerageAcctId = Number(accountNumberRaw);
    const masterAccounts = (this.restrictionSimulatorForm.get('masterAccounts').value || []) as Array<{ value: string }>;
    const normalizedMasterAccounts = masterAccounts
      .map((account) => Number(account.value))
      .filter((account) => Number.isFinite(account));

    return {
      brokerageAcctId: Number.isFinite(brokerageAcctId) ? brokerageAcctId : 0,
      masterAccounts: normalizedMasterAccounts
    };
  }

  private resolveItemIssueId(selectedSecurity: RestrictionSimulatorSearchRecord): number | null {
    const source = selectedSecurity?.securityRow || selectedSecurity;
    const candidate = source?.ssid ?? source?.SSID;

    const itemIssueId = Number(candidate);
    return Number.isFinite(itemIssueId) ? itemIssueId : null;
  }

  private hasMissingItemIssueId(): boolean {
    return this.selectedSecurities.some((security) => !Number.isFinite(security.itemIssueId as number));
  }

  private resolvePutCall(selectedSecurity: RestrictionSimulatorSearchRecord): string {
    const source = selectedSecurity?.securityRow || selectedSecurity;
    const tickerSymbol = (source?.tickerSymbol || '').toString().toUpperCase();

    if (!tickerSymbol) {
      return '';
    }

    // OSI/OCC format: ROOT + YYMMDD + (C|P) + STRIKE (e.g. A 250718P00055000)
    const osiMatch = tickerSymbol.match(/\d{6}([CP])\d{8}/);
    if (osiMatch?.[1]) {
      return osiMatch[1];
    }

    if (/\bCALL\b/.test(tickerSymbol)) {
      return 'C';
    }

    if (/\bPUT\b/.test(tickerSymbol)) {
      return 'P';
    }

    return '';
  }

  private processTradeValidationResponse(response: any): boolean {
    const outputArea = response?.tradeRestriction?.outputArea;
    const restrictCd = outputArea?.restrictCd;
    const returnMessageTx = outputArea?.returnMessageTx;
    const isTimeout = response?.tradeRestriction?.timeOut === true;

    if (isTimeout || restrictCd === 'T') {
      this.clearValidationResultState();
      return true;
    }

    if (restrictCd === 'N' && returnMessageTx === 'Success') {
      this.setValidationResultState('ALLOWED', response);
      return false;
    }

    if (restrictCd === 'Y' && returnMessageTx === 'Success') {
      this.setValidationResultState('RESTRICTED', response);
      return false;
    }

    this.clearValidationResultState();
    return false;
  }

  clearValidationMessages() {
    this.clearValidationResultState();
  }

  private clearValidationResultState() {
    this.validationSummaryStatus = '';
    this.validationSummaryDefaultStatus = '';
    this.validationSummaryRuleName = '';
    this.viewDetailsExpanded = false;
    this.validationDetailRows = [];
  }

  private setValidationResultState(status: 'ALLOWED' | 'RESTRICTED', response?: any) {
    this.validationSummaryDefaultStatus = status;
    this.viewDetailsExpanded = false;
    this.validationDetailRows = this.buildValidationDetailRows(status, response);

    const hasRestrictedRows = this.validationDetailRows.some((row) => row.status === 'RESTRICTED');
    this.validationSummaryStatus = hasRestrictedRows ? 'RESTRICTED' : 'ALLOWED';
    this.validationSummaryRuleName = this.validationSummaryStatus === 'RESTRICTED' && this.isStandardNonOptionsOrder
      ? this.extractSummaryRuleName(response)
      : '';
  }

  private buildValidationDetailRows(status: 'ALLOWED' | 'RESTRICTED', response?: any): RestrictionSimulatorDetailRow[] {
    const applicableRuleMap = this.extractApplicableRuleMap(response);

    return this.selectedSecurities.map((security) => {
      const itemIssueId = Number(security.itemIssueId);
      const action = security.action || this.restrictionSimulatorDomain.action.buy;
      const tickerSymbol = security.tickerSymbol || '--';
      const securityTypeLabel = this.getSecurityTypeMessageLabel(security.securityType);
      const applicableRule = Number.isFinite(itemIssueId) ? applicableRuleMap.get(itemIssueId) : undefined;
      const rowStatus = this.resolveApplicableRuleStatus(status, applicableRule);
      const message = this.resolveApplicableRuleMessage(rowStatus, action, tickerSymbol, securityTypeLabel, applicableRule);

      return {
        itemIssueId: Number.isFinite(itemIssueId) ? itemIssueId : null,
        tickerSymbol,
        action,
        securityTypeLabel,
        status: rowStatus,
        message
      };
    });
  }

  private extractApplicableRuleMap(response: any): Map<number, RestrictionSimulatorApplicableRuleEvaluation> {
    const applicableRuleMap = new Map<number, RestrictionSimulatorApplicableRuleEvaluation>();
    const applicableRules: RestrictionSimulatorApplicableRule[] = Array.isArray(response?.applicableRules)
      ? response.applicableRules
      : [];

    applicableRules.forEach((entry) => {
      const itemIssueId = Number(entry?.ssid);
      if (!Number.isFinite(itemIssueId)) {
        return;
      }

      const rules = Array.isArray(entry?.rules) ? entry.rules : [];
      const ruleNames = rules
        .map((rule) => (rule?.ruleName || '').toString().trim())
        .filter((name) => !!name)
        .filter((name, index, arr) => arr.indexOf(name) === index);
      const normalizedRuleNames = ruleNames.map((name) => name.toLowerCase());
      const hasOverrideFoundInOtherOrg = normalizedRuleNames.includes('override found in other org');
      const hasOverrideFound = normalizedRuleNames.includes('override found');
      const restrictionRuleNames = ruleNames.filter((name) => {
        const normalizedName = name.toLowerCase();
        return normalizedName !== 'override found' && normalizedName !== 'override found in other org';
      });

      applicableRuleMap.set(itemIssueId, {
        hasRuleEntries: rules.length > 0,
        hasOverrideFound,
        hasOverrideFoundInOtherOrg,
        restrictionRuleNames
      });
    });

    return applicableRuleMap;
  }

  private resolveApplicableRuleStatus(
    defaultStatus: 'ALLOWED' | 'RESTRICTED',
    applicableRule?: RestrictionSimulatorApplicableRuleEvaluation
  ): 'ALLOWED' | 'RESTRICTED' {
    if (applicableRule?.hasOverrideFoundInOtherOrg || applicableRule?.hasOverrideFound) {
      return 'ALLOWED';
    }

    if (applicableRule && (applicableRule.restrictionRuleNames.length > 0 || applicableRule.hasRuleEntries)) {
      return 'RESTRICTED';
    }

    return defaultStatus === 'RESTRICTED' ? 'ALLOWED' : defaultStatus;
  }

  private resolveApplicableRuleMessage(
    status: 'ALLOWED' | 'RESTRICTED',
    action: string,
    tickerSymbol: string,
    securityTypeLabel: string,
    applicableRule?: RestrictionSimulatorApplicableRuleEvaluation
  ): string {
    if (
      status === 'ALLOWED'
      && applicableRule?.hasOverrideFoundInOtherOrg
      && (applicableRule?.restrictionRuleNames?.length || 0) > 0
    ) {
      return 'Trade order is allowed due to an override on another organization\'s Master account linked to this account.';
    }

    if (status === 'RESTRICTED') {
      if (applicableRule?.restrictionRuleNames?.length) {
        if (applicableRule.restrictionRuleNames
          .map((name) => name.toLowerCase())
          .includes(this.pcisSuperRestrictionRuleName.toLowerCase())) {
          return this.pcisSuperRestrictionRuleName;
        }
        return `by rule: ${applicableRule.restrictionRuleNames.join(', ')}.`;
      }
      return 'Trade order cannot be executed due to restrictions on another organization\'s Master account linked to this account.';
    }

    return `${action} ${tickerSymbol} (${securityTypeLabel}) has no restrictions.`;
  }

  private extractSummaryRuleName(response: any): string {
    const applicableRuleMap = this.extractApplicableRuleMap(response);
    if (!applicableRuleMap.size) {
      return '';
    }

    const selectedSecurity = this.selectedSecurities[0];
    const selectedItemIssueId = Number(selectedSecurity?.itemIssueId);
    if (Number.isFinite(selectedItemIssueId)) {
      return (applicableRuleMap.get(selectedItemIssueId)?.restrictionRuleNames || []).join(', ');
    }

    return Array.from(applicableRuleMap.values())
      .map((entry) => entry.restrictionRuleNames.join(', '))
      .find((name) => !!name) || '';
  }

  private getSecurityTypeMessageLabel(securityType: string): string {
    const normalized = this.getNormalizedSecurityType(securityType);
    return this.sharedSecurityTypeList.find(({ value }) => value === normalized)?.label
      || (normalized === this.securityTypeCode.fi ? this.emtrConstant.restrictionSimulator.fixedIncomeSecurityTypeLabel : undefined)
      || 'Equity';
  }

  private mapTradeActionToBuyOrSell(action: string): string {
    const normalized = (action || '').trim().toUpperCase();

    if (normalized === this.restrictionSimulatorDomain.action.shortSell.toUpperCase()) {
      return '5';
    }

    if (normalized.startsWith(this.restrictionSimulatorDomain.action.sell.toUpperCase())) {
      return '2';
    }

    return '1';
  }

  private mapStandardOptionsOpenClose(action: string): string {
    const normalized = (action || '').trim().toUpperCase();

    if (normalized.endsWith('CLOSE')) {
      return 'C';
    }

    return 'O';
  }

}
